<template>
  <v-dialog
    :value="show"
    width="1200px"
    persistent
    scrollable
  >
    <v-dialog
      v-model="showAddServerConfirmation"
      width="800px"
      persistent
    >
      <v-card>
        <v-card-title
          class="pa-0"
        >
          <v-toolbar
            dark
            color="primary"
          >
            Add Server to Floor Plan?
          </v-toolbar>
        </v-card-title>
        <v-card-text>
          Would you like to add this server {{ serverName }} to the current Floor Plan?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="$emit('input', false)"
          >
            No
          </v-btn>
          <v-btn
            text
            color="primary"
            @click="assignServer(itemData)"
          >
            Yes 
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-card>
      <v-card-title
        class="pa-0"
      >
        <v-toolbar
          dark
          color="primary"
        >
          <v-btn
            icon
            class="mr-2"
            @click="clickClose(isDirty)"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <span>{{ `${newServer ? 'Add New' : 'Edit'}` }}</span>
          <pre class="title"> Server </pre>
          <span v-if="serverName.trim().length">{{ `- ${serverName}` }}</span>
          <v-spacer />
          <lock-timer
            v-if="!newServer"
            v-bind="getLockTimerSettings(
              [itemData],
              {
                recordType: apiRecordCommand,
                children: true
              },
              () => $emit('input', false)
            )"
          />
          <v-btn
            :color="isDirty ? 'green' : 'primary darken-1'"
            :light="isDirty"
            @click="savePageData"
          >
            Save
          </v-btn>
        </v-toolbar>
      </v-card-title>
      <v-card-text style="height: min-content;">
        <v-form 
          ref="form"
          v-model="formErrors"
        >
          <api-errors :errors="errors" />
          <div v-if="!!awaitingApiResponse">
            <v-progress-linear indeterminate />
            <div class="d-flex justify-center">
              Loading...
            </div>
          </div>
          <div class="pt-2">
            <v-row no-gutters>
              <v-col
                cols="12"
                sm="6"
                md="6"
                class="px-3 pt-3"
              >
                <v-text-field
                  v-model="itemData.firstName"
                  label="First Name *"
                  :disabled="isFormDisabled"
                  required
                  :rules="rules().required('First Name')"
                  clearable
                />
              </v-col>
              <v-spacer />
              <v-boolean
                v-model="itemData.active"
                label="Active"
                :disabled="isFormDisabled"
                class="px-5"
              />
            </v-row>
            <v-row no-gutters>
              <v-col
                cols="12"
                sm="6"
                md="6"
                class="px-3 pt-3"
              >
                <v-text-field
                  v-model="itemData.lastName"
                  label="Last Name *"
                  :disabled="isFormDisabled"
                  required
                  :rules="rules().required('Last Name')"
                  clearable
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
                md="6"
                class="px-3 pt-3"
              >
                <v-text-field
                  v-model="itemData.phone"
                  label="Phone #"
                  :disabled="isFormDisabled"
                  clearable
                />
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col
                cols="12"
                sm="6"
                md="6"
                class="px-3 pt-3"
              >
                <div class="d-flex justify-space-between">
                  <number-field
                    v-model="itemData.maxTables"
                    label="Max Tables *"
                    :rules="[...rules().numeric(1, undefined), ...rules().decimals(0), ...rules().required('Max Tables')]"
                    :disabled="isFormDisabled"
                  />
                  <number-field
                    v-model="itemData.maxCovers"
                    label="Max Covers *"
                    :rules="[...rules().numeric(1, undefined), ...rules().decimals(0), ...rules().required('Max Covers')]"
                    :disabled="isFormDisabled"
                  />
                </div>
              </v-col>
              <v-col
                cols="12"
                sm="6"
                md="6"
                class="px-3 pt-3"
              >
                <v-text-field
                  v-model="itemData.email"
                  label="Email"
                  :disabled="isFormDisabled"
                  clearable
                />
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col
                cols="12"
                sm="6"
                md="6"
                class="px-3 pt-3"
              >
                <key-refresher
                  ref="locationMealPeriodInput"
                  unique-key="location-meal-period-input"
                >
                  <template v-slot="{ refreshKey }">
                    <location-meal-period-linked-input 
                      :key="refreshKey"
                      :server-id="itemData.id"
                      :locations-options="locations"
                      :locations="itemData.locations"
                      :meal-periods="itemData.mealPeriods"
                      :disabled="isFormDisabled"
                      @input:locations="$set(itemData, 'locations', $event)"
                      @input:meal-periods="$set(itemData, 'mealPeriods', $event)"
                    />
                  </template>
                </key-refresher>
              </v-col>
              <v-col
                cols="12"
                sm="6"
                md="6"
                class="px-3 pt-3"
              >
                <v-card 
                  class="pa-0 d-flex justify-space-around"
                  :disabled="isFormDisabled"
                  elevation="0"
                >
                  <div class="flex-column">
                    <div class="v-label theme--light">
                      Color *:
                      <server-initial-icon
                        :server="itemData"
                        size="65"
                      />
                    </div>
                    <text-color-selector 
                      v-model="itemData.textColor"
                      label="Text Color" 
                      class="pt-8"
                    />
                  </div>
                  <v-color-picker 
                    v-model="serverColor"
                    mode="hexa"
                    hide-canvas
                    show-swatches
                    hide-mode-switch
                    hide-inputs
                    swatches-max-height="200px"
                  />
                </v-card>
              </v-col>
              <v-spacer />
            </v-row>
          </div>
          <div class="pa-5" />
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import recordLockHelper from '@/ft-vue-core/mixins/recordLockHelper'
import LocationMealPeriodLinkedInput from '../../configuration/servers/mixins/LocationMealPeriodLinkedInput'
import rules from '@/ft-vue-core/mixins/rules'
import TextColorSelector from '../../configuration/tables/floorPlan/selectedElement/TextColorSelector'
import ServerInitialIcon from './ServerInitialIcon'
export default {
  name: 'EditServerDialog',
  components: {LocationMealPeriodLinkedInput, TextColorSelector, ServerInitialIcon},
  mixins: [rules, recordLockHelper],
  props: {
    newServer: Boolean,
    show: Boolean,
    server: {
      type: Object,
      default () {return {}}
    },
    mealPeriod: Object,
    date: [String, Number],
    forEvent: Boolean,
    eventId: Number,
  },
  data () {
    return {
      apiRecordCommand: 'diningServer',
      isDirty: false,
      itemData: {},
      awaitingApiResponse: 0,
      itemDefault: {
        color: '#1e88e5',
        active: true
      },
      locations: [],
      formErrors: undefined,
      showAddServerConfirmation: false,
    }
  },
  computed: {
    isFormDisabled () {
      return !!this.errors?.length || !!this.awaitingApiResponse
    },
    trimmedLocationsOptions () {
      return this.locations.map(l => {return {locationName: l.name, locationId: l.id}})
    },
    serverColor: {
      get () {
        return this.itemData?.color ?? '#FFFFFF'
      },
      set (newVal) {
        this.$set(this.itemData, 'color', newVal)
      }
    },
    serverName () {
      const {name} = this.itemData
      if (name) {
        return name
      }
      const {firstName, lastName} = this.itemData
      return [firstName, lastName].join(' ')
    },
  },
  watch: {
    show (newVal) {
      if (newVal) {
        this.isDirty = false
        this.loadPageData()
      } else {
        this.awaitingApiResponse = 0
        this.locations = []
        this.$ftAPI().get(this.addResourceErrorHandler({
          command: 'releaseLock',
          params: {record_key: this.itemData.recordLockKey}
        }))
        this.itemData = {}
        this.errors = []
      }
    },
    itemData (newVal, oldVal) {
      if (JSON.stringify(oldVal) !== '{}') this.isDirty = true
    }
  },
  methods: {
    clickClose (isDirty, confirmed) {
      if (isDirty && !confirmed) {
        this.$vPrompt({
          title: 'Unsaved Changes',
          titleIcon: 'mdi-alert-circle',
          titleIconColor: 'warning',
          text: 'Discard unsaved changes to this Server?',
          confirmText: 'Discard',
          confirmAction: () => this.clickClose(isDirty, true),
          cancelAction: () => {}
        })
      } else this.$emit('input', false)
    },
    mapLocations (locations) {
      this.$set(this.itemData, 'locations', [])
      for (let l of locations) {
        if (typeof l === 'number') this.itemData.locations.push({locationId: l})
        else this.itemData.locations.push(l)
      }
    },
    loadPageData () {
      const commands = [
        this.withResourceLockHandler({
          command: this.apiRecordCommand,
          loading: this.isLoading,
          afterLoad: (r) => {
            this.awaitingApiResponse -= 1
            this.itemData = r?.[0] ?? {}
            this.isDirty = false
            for (let key in this.itemDefault) {
              if (this.itemData[key] == null) {
                this.$set(this.itemData, key, this.itemDefault[key])
              }
            }
            this.$refs.locationMealPeriodInput.refresh()
          },
          params: {
            children: true,
            filter: [{field: 'id', operator: 'EQUAL', values: [this.server.id]}]
          }
        }),
        {
          command: 'diningLocation',
          load: this.locations,
          loading: this.isLoading,
          afterLoad: () => {
            this.awaitingApiResponse -= 1
          },
        }
      ]
      this.awaitingApiResponse = commands.length
      this.$ftAPI().get(this.addResourceErrorHandler(commands))
    },
    savePageData () {
      if (this.$refs?.form.validate()) {
        const command = {
          command: this.apiRecordCommand,
          loading: this.isLoading,
          afterLoad: (r) => {
            const result = r?.[0]
            this.itemData = result
            if (result) this.$emit('refresh-server', result)
            if (this.newServer) {
              this.showAddServerConfirmation = true
            } else {
              this.$emit('input', false)
            }
          },
          params: {children: true}
        }
        this.awaitingApiResponse = 1
        this.$ftAPI().put(this.addResourceErrorHandler(command), this.itemData)
      }
    },
    assignServer (server) {
      const errorHandler = (err = {}) => {
        this.$emit('instant-add-error', err.message)
        this.$emit('input', false)
      }
      if (this.forEvent) {
        this.$ftAPI().get({
          command: 'diningEvent',
          error: errorHandler,
          afterLoad: r => {
            let event = r?.[0] ?? {}
            event.servers.push({serverId: server.id, eventId: this.eventId})
            this.$ftAPI().put({
              command: 'diningEvent',
              error: errorHandler,
              afterLoad: () => this.$emit('input', false, true),
              params: {children: true, include_fields: ['servers']}
            }, event)
          },
          params: {
            children: true,
            lock: true,
            filter: [{field: 'id', operator: 'EQUAL', values: [this.eventId]}],
            include_fields: ['servers']
          }
        })
      } else {
        this.$ftAPI().get(this.addResourceErrorHandler({
          command: 'diningMealPeriod',
          error: errorHandler,
          afterLoad: (r) => {
            const mealPeriod = r[0]
            const command = {
              command: 'diningSaveMealPeriodServersByDate',
              error: errorHandler,
              afterLoad: () => this.$emit('input', false, true),
              params: {
                children: true,
                include_fields: ['seatings']
              }
            }
            this.$ftAPI().put(
              this.addResourceErrorHandler(command),
              {
                mealPeriod: mealPeriod,
                date: this.date,
                servers: [
                  ...mealPeriod.servers,
                  {
                    serverId: server.id,
                    date: this.date
                  }
                ]
              }
            )
          },
          params: {
            lock: true,
            children: true,
            filter: [{field: 'id', operator: 'EQUAL', values: [this.mealPeriod.id]}]
          }
        }))
      }
    },
  }
}
</script>

<style scoped>
.sticky-header {
  position: sticky;
  top: 0px;
  z-index: 2;
}
</style>